// src/components/Utils/Sidebar.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Home, ClipboardCheck, FileBadge2, Award, Layers, FileCheck, PenLine, Settings, FolderOpen, CalendarPlus, BookOpen, FolderClock, PenTool, FileText } from "lucide-react";

// CSS is global; you can import here OR rely on CreateStudent importing Upload.css
// import "../Upload.css";

type SidebarProps = {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  activeSection: "dashboard" | "settings";
};

export default function Sidebar({
  sidebarCollapsed,
  toggleSidebar,
  activeSection,
}: SidebarProps) {
  const navigate = useNavigate();

  const menuItems = [
  { text: "Add Student", path: "/bulkUpload" },
  { text: "Batch Configuration", path: "/batch_config" },
  { text: "VALP Certificate", path: "/valp_certificate" },
  { text: "VALP Transcript", path: "/valp/generate" },
  { text: "Dean Signature", path: "/DeanSignature" },
  { text: "Add Academic Year", path: "/terms"},
  { text: "VALP Courses", path: "/valpcourses" },
  { text: "Ongoing VALP Courses", path: "/course_records" },
];

const iconMap: Record<string, JSX.Element> = {
  // Student
  "Add Student": <UserPlus size={22} color="#1e88e5" />,          // Blue

  // Batch
  "Batch Configuration": <Layers size={22} color="#fb8c00" />,   // Orange

  // Certificates & Transcript
  "VALP Certificate": <FileCheck size={22} color="#43a047" />,   // Green
  "VALP Transcript": <FileText size={22} color="#43a047" />,     // Green

  // Dean
  "Dean Signature": <PenTool size={22} color="#6d4c41" />,        // Brown

  // Academic
  "Add Academic Year": <CalendarPlus size={22} color="#2e7d32" />,// Dark Green

  // Courses
  "VALP Courses": <BookOpen size={22} color="#1565c0" />,         // Deep Blue
  "Ongoing VALP Courses": <FolderClock size={22} color="#1565c0" />,// Deep Blue
};

  return (
    <>
      {/* Sidebar Toggle Button */}
      <button
        className={`menuToggleBtn ${
          sidebarCollapsed ? "sidebarCollapsed" : ""
        }`}
        onClick={toggleSidebar}
        aria-label="Toggle sidebar"
      >
        <div className="hamburgerIcon">
          <span className="hamburgerLine" />
          <span className="hamburgerLine" />
          <span className="hamburgerLine" />
        </div>
      </button>

      {/* Sidebar Container */}
      <div className={`sidebar ${sidebarCollapsed ? "collapsed" : ""}`}>
        {/* Logo Section */}
        <div className="logoSection">
          <div
            className="logoCircle d-flex justify-content-center align-items-center shadow-sm mx-auto"
            style={{
              width: "95px", // larger circle
              height: "95px",
              borderRadius: "50%",
              backgroundColor: "#fff",
              border: "2px solid #ddd",
              overflow: "hidden",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "10px", // 🟢 adds inner breathing space to prevent edge cutting
            }}
          >
            <img
              src="/images/IIITB_logo1.png"
              alt="IIITB Logo"
              style={{
                width: "90%",
                height: "90%",
                objectFit: "contain", // 🟢 shows full image, no cropping
                objectPosition: "center", // 🟢 ensures it stays centered horizontally
              }}
            />
          </div>
          <div
            className="instituteName text-center"
            style={{
              fontFamily: "'Merriweather', serif",
              fontWeight: 600,
              fontSize: "1.3rem",
              color: "#161e27ff",
              lineHeight: "1.4",
              letterSpacing: "0.5px",
              whiteSpace: "pre-line", // 👈 this ensures line breaks are respected
              maxWidth: "100%", // prevent tight wrapping
            }}
          >
            {`International Institute of
Information Technology
Bangalore`}
          </div>
        </div>

        {/* Navigation Menu */}
        <div className="navMenu">
          {menuItems.map((item) => (
            <button
              key={item.text}
              className="navItem"
              onClick={() => navigate(item.path)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "14px",
                padding: "12px 14px",
                marginBottom: "12px", // ← margin added here
                borderRadius: "8px",
                width: "100%",
              }}
            >
              <span className="navIcon">{iconMap[item.text]}</span>

              <span
                className="navText"
                style={{
                  fontSize: "17px",
                  fontWeight: 600,
                }}
              >
                {item.text}
              </span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
